import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import feedbackActions from "../../actions/feedbackActions";
import { notifyError } from "../../utils/Notify";
import { getText, getTextServerError } from "../../lang";
import LoadindSpinner from "../../components/LoadingSpinner";
import PageFull from "../../components/PageFull";
import NpsRatePage from "./NpsRatePage";
import NpsLeaveReviewPage from "./NpsLeaveReviewPage";
import NpsFeedbackPage from "./NpsFeedbackPage";
import "./Nps.css";

const convertNumberTo5PointSystemValue = (
  is5StarReviewEnabled = false,
  value = 1,
) => {
  if (is5StarReviewEnabled) {
    return Math.ceil((value || 1) / 2);
  }
  return value;
};

const getStepForVote = (location, vote) =>
  vote >=
  convertNumberTo5PointSystemValue(
    location?.is5StarReviewEnabled,
    location?.minRedirectScore,
  )
    ? 1
    : 2;

const NPS = ({ isPreview = false }) => {
  const { _id } = useParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState("");
  const [selectedRate, setSelectedRate] = useState("");
  const [step, setStep] = useState("");
  const [urls, setUrls] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const languageRef = useRef("en");
  const fetchedIdRef = useRef(null);

  const getFeedbackData = useCallback(
    async (isMounted) => {
      if (_id) {
        setIsLoading(true);
        const result = isPreview
          ? await feedbackActions.getInvitationPreview(_id)
          : await feedbackActions.getInvitation(_id);
        if (result.success) {
          if (isMounted) {
            const data = result.data;
            setInvitation(data);
            languageRef.current = data?.language || "en";

            if (data.vote_value) {
              if (getStepForVote(data.location, data.vote_value) === 1) {
                if (isPreview) {
                  // Preview must not track anything, so the review links come
                  // from the invitation itself instead of POST /yes.
                  setUrls(data?.location?.urls || []);
                  setStep(1);
                } else {
                  const resultPositive =
                    await feedbackActions.sendPositive(_id);
                  if (resultPositive.success) {
                    setStep(1);
                    setUrls(resultPositive.data.urls);
                  } else {
                    notifyError(getTextServerError(resultPositive.errMsg));
                  }
                }
              } else {
                setStep(2);
              }
            } else {
              setStep(0);
            }
          }
        } else {
          notifyError(
            result.errMsg
              ? result.errMsg.startsWith("child")
                ? getText("ERR_GENERAL", languageRef.current)
                : result.errMsg
              : getText("ERR_GENERAL", languageRef.current),
          );
          navigate("/error");
        }
        setIsLoading(false);
      }
    },
    [_id, isPreview, navigate],
  );

  useEffect(() => {
    let isMounted = true;
    if (fetchedIdRef.current !== _id) {
      fetchedIdRef.current = _id;
      getFeedbackData(isMounted);
    }
    return () => {
      isMounted = false;
    };
  }, [_id, getFeedbackData]);

  const handleSelectLink = async (obj) => {
    if (isPreview) {
      // Never count the click as a followed link in preview, but still let the
      // user check where the button leads.
      if (obj.url) {
        window.open(obj.url, "_blank", "noopener,noreferrer");
      } else {
        notifyError(getText("ERR_GENERAL", invitation?.language || "en"));
      }
      return;
    }
    await feedbackActions.selectLink(_id, obj.weight);
    if (obj.url) {
      window.location.href = obj.url;
    } else {
      notifyError(getText("ERR_GENERAL", invitation?.language || "en"));
    }
  };

  const page = isLoading ? (
    <LoadindSpinner />
  ) : (
    <PageFull
      className={`feedback-page-wrapper ${
        step === 1 ? "positive-feedback-page" : ""
      }`}
    >
      {step === 0 && (
        <NpsRatePage
          invitation={invitation}
          selectedRate={selectedRate}
          onClick={
            isPreview
              ? undefined
              : async (index) => {
                  const vote = index + 1;
                  setSelectedRate(index);

                  const resultVote = await feedbackActions.sendVote(_id, vote);
                  if (resultVote.success) {
                    if (getStepForVote(invitation?.location, vote) === 1) {
                      const resultPositive =
                        await feedbackActions.sendPositive(_id);
                      if (resultPositive.success) {
                        setStep(1);
                        setUrls(resultPositive.data.urls);
                      } else {
                        notifyError(getTextServerError(resultPositive.errMsg));
                      }
                    } else {
                      setStep(2);
                    }
                  } else {
                    notifyError(getTextServerError(resultVote.errMsg));
                  }
                }
          }
        />
      )}
      {step === 1 && (
        <NpsLeaveReviewPage
          urls={urls}
          handleSelectLink={handleSelectLink}
          invitation={invitation}
        />
      )}
      {step === 2 && (
        <NpsFeedbackPage
          id={_id}
          isPreview={isPreview}
          onClickBack={() => {
            setStep(0);
            getFeedbackData();
          }}
          onSendButton={() => {
            setStep(3);
          }}
          invitation={invitation}
        />
      )}
      {step === 3 && (
        <>
          <div className="nps-feedback-title strong">
            {getText(
              "TITLE_FEEDBACK_NEG_CONFIRM",
              invitation?.language || "en",
            )}
          </div>
          <div className="nps-feedback-title">
            {getText(
              "TEXT_FEEDBACK_NEG_HELP_CONFIRM",
              invitation?.language || "en",
            )}
          </div>
        </>
      )}
    </PageFull>
  );

  if (!isPreview) {
    return page;
  }

  const previewLang = invitation?.language || "en";

  return (
    <div className="nps-preview-layout">
      <div className="nps-preview-bar" role="status">
        <span className="nps-preview-bar-label">
          {getText("TITLE_PREVIEW_MODE", previewLang)}
        </span>
        <span className="nps-preview-bar-text">
          {getText("TEXT_PREVIEW_MODE_INFO", previewLang)}{" "}
          <strong>{getText("TEXT_PREVIEW_MODE_WARNING", previewLang)}</strong>
        </span>
      </div>
      {page}
    </div>
  );
};

export default NPS;
